import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import api from '../utils/api';
import useCartStore from '../store/useCartStore';
import useAuthStore from '../store/useAuthStore';
import { KurtiPlaceholder } from '../components/ProductCard';
import { AuroraBlobs, GrainOverlay, GlassChip, MagneticButton, Reveal, fadeUp, stagger } from '../components/ui';
import { FiCheck, FiEdit2, FiShield } from 'react-icons/fi';

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

const PAYMENT_METHODS = [
  { id: 'Razorpay', label: 'Razorpay', sub: 'Cards · UPI · NetBanking · Wallets', icon: '💳' },
  { id: 'UPI', label: 'UPI', sub: 'Pay via any UPI app', icon: '📲' },
  { id: 'COD', label: 'Cash on Delivery', sub: 'Pay when you receive', icon: '💵' },
];

const INDIAN_STATES = ['Andaman and Nicobar Islands','Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chandigarh','Chhattisgarh','Dadra and Nagar Haveli and Daman and Diu','Delhi','Goa','Gujarat','Haryana','Himachal Pradesh','Jammu and Kashmir','Jharkhand','Karnataka','Kerala','Ladakh','Lakshadweep','Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Puducherry','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal'];

export default function CheckoutPage() {
  const { cart, totals, fetchCart } = useCartStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const [step, setStep] = useState('address');
  const [payMethod, setPayMethod] = useState('Razorpay');
  const [placing, setPlacing] = useState(false);
  const [address, setAddress] = useState({
    name: user?.name || '', phone: user?.phone?.replace('+91', '') || '',
    line1: '', line2: '', city: '', state: '', pincode: '',
  });

  useEffect(() => { fetchCart(); }, []);

  const items = cart?.items || [];

  const handlePlaceOrder = async () => {
    setPlacing(true);
    try {
      const orderData = {
        items: items.map(item => ({ product: item.product._id, size: item.size, color: item.color, qty: item.qty })),
        address: { ...address, phone: `+91${address.phone}` },
        payment: { method: payMethod },
        couponCode: cart?.coupon?.code || '',
      };
      const data = await api.post('/orders', orderData);
      const order = data.order;

      if (payMethod === 'COD') {
        toast.success('Order placed successfully!');
        navigate(`/orders/${order._id}/track`);
        return;
      }

      const loaded = await loadRazorpayScript();
      if (!loaded) { toast.error('Payment gateway failed to load. Please try again.'); setPlacing(false); return; }

      const rpData = await api.post('/payment/create-razorpay-order', { orderId: order._id });

      const options = {
        key: rpData.key,
        amount: rpData.amount,
        currency: 'INR',
        name: 'VritiEthnic',
        description: `Order ${order.orderId}`,
        order_id: rpData.razorpayOrderId,
        prefill: { name: user?.name, contact: user?.phone, email: user?.email },
        theme: { color: '#7A1F2B' },
        handler: async (response) => {
          try {
            await api.post('/payment/verify', {
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
              orderId: order._id,
            });
            toast.success('Payment successful!');
            navigate(`/orders/${order._id}/track`);
          } catch {
            toast.error('Payment verification failed. Contact support.');
          }
        },
        modal: { ondismiss: () => { setPlacing(false); toast.error('Payment cancelled'); } },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      setPlacing(false);
      toast.error(err.message || 'Failed to place order');
    }
  };

  return (
    <div>
      {/* Hero strip */}
      <section style={{ position: 'relative', background: '#1A100E', overflow: 'hidden', padding: '48px 24px 40px' }}>
        <AuroraBlobs muted />
        <GrainOverlay />
        <div style={{ position: 'relative', zIndex: 2, maxWidth: 1100, margin: '0 auto' }}>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <GlassChip>Final step</GlassChip>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} className="serif" style={{ fontSize: 'clamp(34px, 4.4vw, 52px)', color: '#FFF', lineHeight: 1, letterSpacing: '-0.02em', marginTop: 18 }}>
            Checkout
          </motion.h1>
        </div>
      </section>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 24px 64px' }}>
        <div className="checkout-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 32, alignItems: 'flex-start' }}>
          <div>
            {/* Progress bar */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} style={{
              display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28, padding: '14px 18px',
              background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(229,219,200,0.6)', borderRadius: 999,
              backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
            }}>
              {['address', 'payment'].map((s, i) => {
                const done = step === 'payment' && i === 0;
                const active = step === s;
                return (
                  <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, cursor: i === 0 ? 'pointer' : 'default' }} onClick={() => i === 0 && setStep('address')}>
                    <motion.span animate={{ scale: active ? 1.1 : 1 }} style={{
                      width: 28, height: 28, borderRadius: '50%',
                      background: done || active ? 'var(--primary)' : 'var(--line)',
                      color: done || active ? '#FFF' : 'var(--muted)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 700,
                    }}>
                      {done ? <FiCheck size={14} /> : i + 1}
                    </motion.span>
                    <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: active ? 'var(--ink)' : 'var(--muted)' }}>{s === 'address' ? 'Delivery' : 'Payment'}</span>
                    {i === 0 && <div style={{ flex: 1, height: 1, background: 'var(--line)', marginLeft: 8 }} />}
                  </div>
                );
              })}
            </motion.div>

            <AnimatePresence mode="wait">
              {step === 'address' && (
                <motion.div key="address" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.3 }}
                  className="ve-glass-form-card" style={{ padding: 28 }}>
                  <h2 className="serif" style={{ fontSize: 24, marginBottom: 6 }}>Delivery Address</h2>
                  <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 22 }}>Where should we send your kurtis?</p>
                  <motion.div variants={stagger} initial="hidden" animate="show" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <motion.div variants={fadeUp}>
                      <label className="fld-lab">Full Name</label>
                      <input className="ve-fld" value={address.name} onChange={e => setAddress(p => ({ ...p, name: e.target.value }))} placeholder="Priya Sharma" />
                    </motion.div>
                    <motion.div variants={fadeUp}>
                      <label className="fld-lab">Phone</label>
                      <div style={{ display: 'flex', border: '1px solid var(--line)', borderRadius: 12, overflow: 'hidden', background: 'rgba(255,255,255,0.6)' }}>
                        <span style={{ padding: '0 12px', background: 'rgba(42,31,26,0.05)', display: 'flex', alignItems: 'center', fontSize: 13, fontWeight: 700, borderRight: '1px solid var(--line)' }}>+91</span>
                        <input value={address.phone} onChange={e => setAddress(p => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))} placeholder="98XXXXXXXX" style={{ flex: 1, height: 48, padding: '0 12px', border: 'none', outline: 'none', fontSize: 14, background: 'transparent', fontFamily: 'inherit' }} />
                      </div>
                    </motion.div>
                    <motion.div variants={fadeUp} style={{ gridColumn: '1/-1' }}>
                      <label className="fld-lab">Address Line 1</label>
                      <input className="ve-fld" value={address.line1} onChange={e => setAddress(p => ({ ...p, line1: e.target.value }))} placeholder="House / Flat, Building, Street" />
                    </motion.div>
                    <motion.div variants={fadeUp} style={{ gridColumn: '1/-1' }}>
                      <label className="fld-lab">Address Line 2 <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(optional)</span></label>
                      <input className="ve-fld" value={address.line2} onChange={e => setAddress(p => ({ ...p, line2: e.target.value }))} placeholder="Area, Landmark" />
                    </motion.div>
                    <motion.div variants={fadeUp}>
                      <label className="fld-lab">City</label>
                      <input className="ve-fld" value={address.city} onChange={e => setAddress(p => ({ ...p, city: e.target.value }))} placeholder="Bengaluru" />
                    </motion.div>
                    <motion.div variants={fadeUp}>
                      <label className="fld-lab">State</label>
                      <select className="ve-fld" value={address.state} onChange={e => setAddress(p => ({ ...p, state: e.target.value }))}>
                        <option value="">Select state</option>
                        {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </motion.div>
                    <motion.div variants={fadeUp}>
                      <label className="fld-lab">PIN Code</label>
                      <input className="ve-fld" value={address.pincode} onChange={e => setAddress(p => ({ ...p, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) }))} placeholder="560078" />
                    </motion.div>
                  </motion.div>
                  <div style={{ marginTop: 24 }}>
                    <MagneticButton variant="primary" size="lg" onClick={() => {
                      if (!address.name || !address.phone || address.phone.length !== 10 || !address.line1 || !address.city || !address.state || address.pincode.length !== 6) {
                        toast.error('Fill in all required fields'); return;
                      }
                      setStep('payment');
                    }}>Continue to payment ›</MagneticButton>
                  </div>
                </motion.div>
              )}

              {step === 'payment' && (
                <motion.div key="payment" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.3 }}
                  className="ve-glass-form-card" style={{ padding: 28 }}>
                  <h2 className="serif" style={{ fontSize: 24, marginBottom: 6 }}>Payment Method</h2>
                  <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 22 }}>All payments are secured and encrypted.</p>

                  <motion.div variants={stagger} initial="hidden" animate="show" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {PAYMENT_METHODS.map(m => {
                      const selected = payMethod === m.id;
                      return (
                        <motion.button
                          key={m.id} variants={fadeUp}
                          onClick={() => setPayMethod(m.id)}
                          whileHover={{ y: -2 }} whileTap={{ scale: 0.99 }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 16, padding: 18, cursor: 'pointer',
                            border: `2px solid ${selected ? 'var(--primary)' : 'rgba(229,219,200,0.7)'}`,
                            borderRadius: 16,
                            background: selected ? 'linear-gradient(135deg, rgba(243,226,225,0.7), rgba(255,255,255,0.6))' : 'rgba(255,255,255,0.5)',
                            textAlign: 'left', fontFamily: 'inherit',
                          }}
                        >
                          <motion.span animate={{ scale: selected ? 1.1 : 1 }} style={{
                            width: 22, height: 22, borderRadius: '50%',
                            border: `2px solid ${selected ? 'var(--primary)' : 'var(--line)'}`,
                            background: selected ? 'var(--primary)' : 'transparent',
                            flexShrink: 0, position: 'relative',
                          }}>
                            {selected && <span style={{ position: 'absolute', inset: 4, borderRadius: '50%', background: '#FFF' }} />}
                          </motion.span>
                          <span style={{ fontSize: 22 }}>{m.icon}</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>{m.label}</div>
                            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2, letterSpacing: '0.04em' }}>{m.sub}</div>
                          </div>
                        </motion.button>
                      );
                    })}
                  </motion.div>

                  <div style={{ marginTop: 24, padding: 16, background: 'rgba(255,255,255,0.45)', border: '1px solid var(--line-2)', borderRadius: 14, fontSize: 13 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontWeight: 700, fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--muted)' }}>Delivering to</span>
                      <button onClick={() => setStep('address')} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', background: 'transparent', color: 'var(--primary)', fontSize: 11, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                        <FiEdit2 size={11} /> Edit
                      </button>
                    </div>
                    <div style={{ color: 'var(--ink-2)', lineHeight: 1.7 }}>
                      <strong style={{ color: 'var(--ink)' }}>{address.name}</strong> · +91 {address.phone}<br />
                      {address.line1}{address.line2 ? `, ${address.line2}` : ''}<br />
                      {address.city}, {address.state} — {address.pincode}
                    </div>
                  </div>

                  <div style={{ marginTop: 24 }}>
                    <MagneticButton variant="primary" size="lg" onClick={handlePlaceOrder} disabled={placing} style={{ width: '100%' }}>
                      {placing ? 'Placing order…' : payMethod === 'COD' ? 'Place order (COD) ›' : `Pay ₹${totals.total?.toLocaleString('en-IN')} ›`}
                    </MagneticButton>
                  </div>

                  <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 11, color: 'var(--muted)', letterSpacing: '0.08em' }}>
                    <FiShield size={12} /> Secured by Razorpay · 256-bit SSL
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Order summary */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
            style={{
              position: 'sticky', top: 96,
              background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(229,219,200,0.65)', borderRadius: 20,
              backdropFilter: 'blur(24px) saturate(160%)', WebkitBackdropFilter: 'blur(24px) saturate(160%)',
              padding: 24, boxShadow: '0 12px 32px rgba(42,31,26,0.06)',
            }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 18 }}>Your order</div>
            <div style={{ maxHeight: 320, overflowY: 'auto', marginBottom: 12 }}>
              {items.map(item => (
                <div key={item._id} style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                  <div style={{ width: 60, height: 76, borderRadius: 8, overflow: 'hidden', flexShrink: 0, background: 'var(--surface-2)' }}>
                    {item.product?.images?.[0]?.url
                      ? <img src={item.product.images[0].url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <KurtiPlaceholder color={item.color?.hex || '#7A1F2B'} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="serif" style={{ fontSize: 14, lineHeight: 1.25 }}>{item.product?.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>Size {item.size} · Qty {item.qty}</div>
                    <div style={{ fontSize: 13, fontWeight: 700, marginTop: 4 }}>₹{(item.product?.price * item.qty)?.toLocaleString('en-IN')}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ borderTop: '1px dashed var(--line-2)', paddingTop: 14 }}>
              {[['Subtotal', `₹${totals.subtotal?.toLocaleString('en-IN')}`], ['Discount', totals.discount > 0 ? `–₹${totals.discount}` : '—'], ['Shipping', totals.shipping === 0 ? 'Free' : `₹${totals.shipping}`]].map(([k, v]) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                  <span style={{ color: 'var(--ink-2)' }}>{k}</span>
                  <span style={{ fontWeight: 700, color: v === 'Free' || (typeof v === 'string' && v.startsWith('–')) ? 'var(--ok)' : 'var(--ink)' }}>{v}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--line-2)' }}>
                <span style={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Total</span>
                <span className="serif" style={{ fontSize: 26, fontWeight: 600 }}>₹{totals.total?.toLocaleString('en-IN')}</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .checkout-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
